import type { QuoteLineItemKind, QuoteLineItemView } from "@/services/quoteLineItems";
import { apiClient } from "@/services/apiClient";
import type { ProfessionalPricingDto } from "@/services/professionalEarningsService";
import type { QuoteClarification } from "@/services/quoteRequestsService";

export type { ProfessionalPricingDto } from "@/services/professionalEarningsService";

export type NotarialRequestStatus =
  // Post-payment "complete profile" gate. The backend parks newly-paid
  // requests here when the consumer doesn't yet have an avatar + verified
  // phone, so professionals never see them in their queue. Promotion to
  // `pending` happens automatically once the user completes their profile
  // (POST /auth/me/avatar or /auth/me/phone/verify-code) — see the
  // complete-profile-page for the UI side.
  | "pending_profile_completion"
  // Paid by bank transfer, money not confirmed yet: the receipt either failed
  // the code-side match or is still being extracted. Professionals never see
  // it (their open pool is status = 'pending' alone); the consumer does, so
  // they know the request exists and why it is not moving. Promotes to
  // `pending` automatically when the transfer is confirmed or an admin
  // approves it.
  | "pending_payment_verification"
  | "pending"
  | "in_progress"
  | "completed"
  | "missing_information"
  | "cannot_complete"
  | "document_ready_at_professional"
  | "document_at_platform"
  | "awaiting_consumer_delivery_details"
  | "awaiting_shipment_to_consumer"
  | "in_transit_to_consumer";

export type ProfessionalFulfillmentStage =
  | "at_platform"
  | "awaiting_consumer_delivery_details"
  | "awaiting_shipment_to_consumer"
  | "in_transit_to_consumer"
  | "delivered_to_consumer";

/** A pro's own bid status on a quote_request row in the merged list — mirrors QuoteStatus on the backend. */
export type MyQuoteStatus = "submitted" | "withdrawn" | "awarded" | "rejected" | "expired";

export interface NotarialRequestListItem {
  /**
   * The department this job's document ships to, and whether the viewing
   * professional covers it (spec 8ec68023). Both absent on non-RFQ rows and on
   * digital jobs.
   */
  deliveryAdminArea?: string | null;
  isLocalToMe?: boolean;
  id: string;
  requestedByUserId: string;
  applicantName: string | null;
  applicantEmail: string | null;
  /** `'quoting'` only ever appears on a `channel: 'quote'` row — never a real trámite status. */
  status: NotarialRequestStatus | "quoting";
  fulfillmentStage: ProfessionalFulfillmentStage | null;
  requestType: string;
  /** The original RFQ's title, only set for a spawned RFQ child (requestType 'custom_quote'). */
  sourceRequestTitle: string | null;
  acceptedByUserId: string | null;
  acceptedAt: string | null;
  createdAt: string;
  updatedAt: string;
  documentCount: number;
  pricing: ProfessionalPricingDto | null;
  /**
   * Set while the platform has work on this request stopped. Null on quote
   * rows, which are a different entity and cannot be paused.
   */
  pausedAt: string | null;
  /** Present and `'quote'` only for a broadcast quote_request row merged into this list. */
  channel?: "quote";
  /** When this leg's quoting window closes. Quote rows only. */
  quotingExpiresAt?: string | null;
  myQuoteStatus?: MyQuoteStatus | null;
  /**
   * This professional's own question thread on the row's leg: 'answered' when
   * the client has replied, 'awaiting' while it is still open. Not derivable
   * from `status`, which stays 'quoting' either way.
   */
  myClarificationStatus?: "answered" | "awaiting" | null;
  myQuotePriceMinor?: number | null;
  /**
   * The RFQ this trámite was SPAWNED FROM, when it was spawned from one.
   *
   * The ID is what grouping keys off — the title beside it is a display
   * string, and two RFQs can share one. Null on a trámite the consumer
   * ordered directly, and on a quote row, which IS a parent rather than
   * spawned from one.
   */
  sourceRequestId?: string | null;
}

export interface NotarialRequestListResponse {
  items: NotarialRequestListItem[];
  total: number;
}

/** Which side of the request sent a document. */
export type RequestDocumentUploaderRole = "consumer" | "professional";

/**
 * Document types only a PROFESSIONAL sends, in the order the picker offers
 * them: the common case first, the catch-all last.
 *
 * Mirrors PROFESSIONAL_ATTACHMENT_DOCUMENT_TYPES on the backend, which is the
 * authority — these are deliberately absent from the consumer document enum,
 * so a consumer can neither upload nor be asked for one.
 */
export const PROFESSIONAL_ATTACHMENT_DOCUMENT_TYPES = [
  "para_firmar",
  "borrador",
  "instrucciones",
  "otro_profesional",
] as const;

export type ProfessionalAttachmentDocumentType =
  (typeof PROFESSIONAL_ATTACHMENT_DOCUMENT_TYPES)[number];

export interface NotarialRequestDocumentDto {
  id: string;
  documentType: string;
  fileUrl: string;
  originalFilename: string;
  /**
   * Who sent it. Group on this, never on
   * `documentType === "notarial_certificate"` — that test only worked while
   * the professional could send exactly one kind of file.
   */
  uploaderRole: RequestDocumentUploaderRole;
  note: string | null;
  contentType: string;
  createdAt: string;
}

export type NotarialRequestEventType =
  | "created"
  | "accepted"
  | "info_requested"
  | "resubmitted"
  | "certificate_uploaded"
  | "certificate_available"
  /** The professional replaced an already-delivered document. */
  | "certificate_replaced"
  /** The requester says the delivered document is wrong. */
  | "correction_requested"
  | "cannot_complete"
  | "physical_document_ready"
  | "physical_pickup_details_updated"
  | "document_ready_for_delivery"
  | "platform_received_document"
  | "consumer_delivery_requested"
  | "consumer_delivery_details_submitted"
  | "consumer_delivery_details_updated"
  | "shipped_to_consumer"
  | "shipment_in_transit"
  | "delivered_to_consumer"
  | "estimated_date_set"
  | "estimated_date_changed"
  | "document_uploaded"
  | "document_deleted";

export type NotarialRequestEventActorRole =
  | "notary"
  | "accountant"
  | "company_user"
  | "consumer"
  | "platform_admin";

export interface NotarialRequestEventDto {
  id: string;
  eventType: NotarialRequestEventType | string;
  actorUserId: string | null;
  actorDisplayName: string | null;
  actorRole: NotarialRequestEventActorRole | string | null;
  message: string | null;
  createdAt: string;
}

export interface NotarialRequestDetail {
  id: string;
  requestType: string;
  sourceRequestTitle: string | null;
  tenantId: string;
  tenantName: string | null;
  tenantRut: string | null;
  tenantAddress: string | null;
  tenantCity: string | null;
  tenantDepartment: string | null;
  // tenantPhone / tenantEmail / requesterEmail are GONE from the
  // professional-facing response on purpose — contact between a consumer and
  // a professional goes through the platform. Declaring them here after the
  // server stopped sending them is the specific trap this codebase has hit
  // before: an untyped HTTP response means tsc keeps believing the type, so
  // the field reads as `undefined` at runtime and nothing fails at build.
  requestedByUserId: string;
  requesterName: string | null;
  requesterDocumentType: string | null;
  requesterDocumentNumber: string | null;
  status: NotarialRequestStatus;
  fulfillmentStage: ProfessionalFulfillmentStage | null;
  acceptedByUserId: string | null;
  acceptedAt: string | null;
  estimatedCompletionDate: string | null;
  certificatePathChoice: string | null;
  missingInfoMessage: string | null;
  missingDocumentNotes: Record<string, string> | null;
  cannotCompleteNotes: string | null;
  certificateValidityDate: string | null;
  /**
   * A correction the requester asked for after delivery. `correctionOpen` is
   * served rather than derived: the status still reads "completed" while one
   * is pending, so it is not inferable client-side.
   */
  correctionRequestedAt: string | null;
  correctionReason: string | null;
  correctionOpen: boolean;
  /**
   * Whether the upload form should be offered, and which way in applies:
   * "initial", "window" (the professional's own 24h replacement) or
   * "correction" (the requester asked). Decided by the server with the same
   * function the upload endpoint enforces — do NOT re-derive it here, which is
   * how the old `status === "in_progress"` check drifted from the gate.
   */
  canUploadDocument: boolean;
  uploadMode: "initial" | "window" | "correction" | null;
  pickupAddressSnapshot: string | null;
  deliveryAddressSnapshot: string | null;
  createdAt: string;
  updatedAt: string;
  documents: NotarialRequestDocumentDto[];
  events: NotarialRequestEventDto[];
  incomeCertificateDetails?: Record<string, unknown> | null;
  fulfillmentMode?: string;
  pricing: ProfessionalPricingDto | null;
  /** Company RUT was not verified with the registry at submission time. */
  companyRutUnverified?: boolean;
  /**
   * Work stopped by the platform, and the admin's reason for it.
   *
   * Ten actions on this request answer 409 `request_paused` while this is set,
   * so the page shows the state and locks the controls instead of letting the
   * professional discover it one refusal at a time. Orthogonal to `status`:
   * nothing else in this payload implies it.
   */
  pausedAt: string | null;
  pauseReason: string | null;
}

export function listNotaryRequests(params: {
  status?: NotarialRequestStatus;
  statuses?: NotarialRequestStatus[];
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<NotarialRequestListResponse> {
  const searchParams = new URLSearchParams();
  const combined = new Set<string>();
  if (params.status) combined.add(params.status);
  params.statuses?.forEach((s) => combined.add(s));
  if (combined.size > 0) searchParams.set("status", Array.from(combined).join(","));
  if (params.type) searchParams.set("type", params.type);
  if (params.limit != null) searchParams.set("limit", String(params.limit));
  if (params.offset != null) searchParams.set("offset", String(params.offset));
  const query = searchParams.toString();
  return apiClient
    .get<NotarialRequestListResponse>(`/notary/requests${query ? `?${query}` : ""}`)
    .then((res) => res.data);
}

export function getNotaryRequestDetail(id: string): Promise<NotarialRequestDetail> {
  return apiClient.get<NotarialRequestDetail>(`/notary/requests/${id}`).then((res) => res.data);
}

// --- Quote/RFQ bidding — GET /pro/quote-requests/:id, submit, withdraw. ---

export interface ProQuoteRequestDetail {
  id: string;
  title: string;
  description: string;
  discipline: "notary" | "accountant";
  quotingExpiresAt: string | null;
  /**
   * When the CONSUMER loses the ability to award — the bidding deadline plus
   * the award grace. Null on legs broadcast before the column shipped, which
   * is why every use of it is conditional.
   */
  awardDeadlineAt: string | null;
  /**
   * What the consumer will be charged to ship this request's document, priced
   * by DESTINATION (spec 8c2aab49 D4). `zone: null` means their address could
   * not be zoned, and the form must then show no shipping rather than the
   * cheaper tier (C-3).
   */
  /**
   * Whether this trámite ends in paper, as the INTAKE decided (spec 8c2aab49
   * D2). The professional no longer declares this — the consumer was asked,
   * gave an address, and every quote they compare carries the shipping.
   */
  producesPhysicalDocument?: boolean;
  delivery?: {
    zone: string | null;
    feeMinor: number;
    includesIva: boolean;
    /**
     * THE FULL ADDRESS, from spec f462e7d6 3.3 on. This used to be the
     * departamento alone, on the argument that the street address is contact
     * information and the awarded professional would receive it "through
     * fulfilment" — and there is no fulfilment leg any more. A professional
     * pricing a delivery they will make themselves is pricing a trip, which a
     * department does not describe.
     *
     * Still no phone and no email: the address is what delivering needs, a
     * contact channel is not.
     */
    adminArea: string | null;
    formattedAddress?: string | null;
    city?: string | null;
    countryCode?: string | null;
    courierNotes?: string | null;
    /**
     * WHERE it is, snapshotted on the request (spec b73fdd78). Absent on a
     * row whose point is not known yet, which is when the card falls back to
     * geocoding the text itself.
     */
    latitude?: number | null;
    longitude?: number | null;
  };
  /** What the consumer attached at intake — what the bid should be priced against. */
  attachments?: Array<{
    id: string;
    originalFilename: string;
    contentType: string;
    documentType: string | null;
    createdAt: string;
  }>;
  myQuote: {
    id: string;
    priceMinor: number;
    /** Their own breakdown read back to them. Null on pre-breakdown quotes. */
    lineItems: QuoteLineItemView[] | null;
    pricesIncludeIva: boolean;
    /**
     * THEIR OWN physicality declaration, read back (spec f462e7d6 3.6).
     * Without it, reopening a bid to edit the price would reset the control to
     * what triage guessed and silently undo a correction the professional had
     * already made.
     */
    requiresPhysicalDelivery?: boolean;
    currency: string;
    estimatedDays: number | null;
    message: string | null;
    status: MyQuoteStatus;
    /**
     * Whether the consumer actually PAID, which `status` does not say: a quote
     * reads 'awarded' from the moment it is chosen, and an unpaid award still
     * lapses back to quoting.
     *
     * The server now also MASKS an unpaid award, reporting it as 'submitted'
     * to professionals, so `status` alone can no longer celebrate early. This
     * stays as the explicit signal rather than an inference.
     */
    awardPaid?: boolean;
    /**
     * May this quote still be edited or withdrawn?
     *
     * The server's own refusals in one boolean: submitOrUpdate needs the leg
     * to be 'quoting' and withdrawQuote needs the quote to be 'submitted'. An
     * awarded leg fails both while still reporting 'submitted' under the mask
     * above, so the window being open is no longer enough to offer the
     * controls — without this the professional would learn of the award from a
     * 400, which is the news we are deliberately withholding.
     *
     * Absent reads as TRUE, for the older-server case.
     */
    canModify?: boolean;
  } | null;
  /**
   * Timeline, oldest first. Server-filtered for this professional: stage events
   * plus their OWN quote activity. A rival's bid never appears here.
   */
  events?: ProQuoteRequestEvent[];
  /**
   * The OTHER disciplines quoting this same request. Non-empty means the
   * professional is being asked to price one half of the job, and the
   * description they are reading covers both.
   */
  otherDisciplines?: Array<"notary" | "accountant">;
  /**
   * The intake's own explanation of how the work splits between disciplines.
   * Null for requests created before the conversational intake, or by an admin.
   */
  scopeRationale?: string | null;
  /** What the intake told the consumer to provide, and why it matters. */
  anticipatedDocuments?: Array<{
    label: string;
    /** 'file' was attached (or not); 'text' was typed — see intakeDetails. */
    kind: "file" | "text";
    documentType: string | null;
    why: string;
  }>;
  /**
   * Values the consumer TYPED into the intake's text items — an RUT, a número
   * de BPS (spec 13810fd2). Keyed by the item's label, which is how they pair
   * back to the question in `anticipatedDocuments`.
   */
  intakeDetails?: Array<{ label: string; value: string }>;
  /**
   * The public question thread for this leg. Every bidder sees the same one,
   * and no entry says who asked — except the reader's own, which are marked so
   * they can tell their blocked attempts from the delivered ones.
   */
  clarifications?: QuoteClarification[];
}

export interface ProQuoteRequestEvent {
  id: string;
  eventType: string;
  actorDisplayName: string | null;
  actorRole: string | null;
  message: string | null;
  createdAt: string;
}

/** Private Blob storage, so the file is proxied through the API like the consumer's. */
export function downloadProQuoteRequestAttachment(
  quoteRequestId: string,
  attachmentId: string,
): Promise<Blob> {
  return apiClient
    .get<Blob>(`/pro/quote-requests/${quoteRequestId}/attachments/${attachmentId}/file`, {
      responseType: "blob",
    })
    .then((res) => res.data);
}

/** What a professional may ask a consumer for: the document-type enum minus the professional's own deliverable. */
export const REQUESTABLE_DOCUMENT_TYPES = [
  "informacion_registral_bps_dgi",
  "informacion_registral_bps",
  "informacion_registral_dgi",
  "comunicacion_bcu",
  "estatuto",
  "estatuto_otros",
  "legal_document_front",
  "legal_document_back",
  "passport_document",
  "income_dgi_certificate",
  "income_dgi_form_6906",
  "income_bps_certificate",
  "income_tarjeta_rut",
  "income_professional_caja_certificate",
  "income_salary_receipt",
  "income_invoice",
  "income_special_format_template",
  "otra_documentacion_notarial",
] as const;

export type RequestableDocumentType = (typeof REQUESTABLE_DOCUMENT_TYPES)[number];

export interface DocumentRequestPayloadItem {
  documentType?: RequestableDocumentType;
  customLabel?: string;
  why?: string;
}

/**
 * Ask the consumer for specific documents. One call for the whole list — the
 * server flips the request to `missing_information` once and notifies once.
 */
export function createProDocumentRequests(
  requestId: string,
  documents: DocumentRequestPayloadItem[],
): Promise<{ requested: number; status: string }> {
  return apiClient
    .post(`/notary/requests/${requestId}/document-requests`, { documents })
    .then((res) => res.data);
}

export interface ProDocumentRequest {
  id: string;
  documentType: string | null;
  customLabel: string | null;
  why: string | null;
  status: "pending" | "fulfilled" | "cancelled";
  fulfilledAt: string | null;
}

/** Withdraw a pending slot. Cancelled, not deleted — the consumer may already have seen it. */
export function cancelProDocumentRequest(
  requestId: string,
  documentRequestId: string,
): Promise<void> {
  return apiClient
    .delete(`/notary/requests/${requestId}/document-requests/${documentRequestId}`)
    .then(() => undefined);
}

export function getProDocumentRequests(
  requestId: string,
): Promise<{ documents: ProDocumentRequest[] }> {
  return apiClient
    .get<{ documents: ProDocumentRequest[] }>(`/notary/requests/${requestId}/document-requests`)
    .then((res) => res.data);
}

export function getQuoteRequestDetailForPro(id: string): Promise<ProQuoteRequestDetail> {
  return apiClient.get<ProQuoteRequestDetail>(`/pro/quote-requests/${id}`).then((res) => res.data);
}

export interface ProQuoteFormConfig {
  /**
   * The platform's fee on the honorarios, ADDED to what the consumer pays
   * (spec 76e23408 D3). Renamed from `commissionPercent`, which served the
   * withheld rate that migration 099 zeroes — left unrenamed this field would
   * have quietly started reporting 0%.
   */
  platformFeePercent: number;
  /**
   * Delivery is NOT here any more. It is priced by destination (spec 8c2aab49
   * D4) and this config is request-independent, so it moved to the per-request
   * detail payload, which knows where the parcel is going.
   */
  dgi: { urValueMinor: number | null; referenceLabel: string | null };
  cajaNotarial: { urValueMinor: number | null; referenceLabel: string | null };
}

/**
 * What the bidding form needs that is not about one request: the commission
 * rate and both UR indices. `urValueMinor: null` means the index is down —
 * the same condition the server refuses a UR line on.
 */
export function getProQuoteFormConfig(): Promise<ProQuoteFormConfig> {
  return apiClient
    .get<ProQuoteFormConfig>("/pro/quote-requests/quote-form-config")
    .then((res) => res.data);
}

/** One line of a quote's breakdown — mirrors the backend `QuoteLineItemDto`. */
export interface QuoteLineItemPayload {
  description: string;
  kind: QuoteLineItemKind;
  /** Fixed amount in centésimos. Omit when the line is denominated in UR. */
  amountMinor?: number;
  urUnits?: number;
  urType?: "dgi" | "caja_notarial";
}

/**
 * No `priceMinor`: the total is the sum of the lines and the server computes
 * it (spec rfq-structured-quotes §3b), so the client cannot send a total that
 * disagrees with its own itemisation.
 */
export interface SubmitQuotePayload {
  lineItems: QuoteLineItemPayload[];
  pricesIncludeIva?: boolean;
  /**
   * Declares that the job yields a physical document. Unlike pricesIncludeIva
   * this changes the consumer's total: the platform's delivery fee is added
   * once when any awarded quote on the request sets it.
   */
  requiresPhysicalDelivery?: boolean;
  estimatedDays?: number;
  message?: string;
  /**
   * What this professional needs from the consumer to do the job (spec
   * 18c82ff1). Seeded from the intake's `anticipatedDocuments` and amended by
   * the professional.
   *
   * `source` is NOT sent: the server decides provenance against the intake
   * draft, because a client that could assert "anticipated" could dress its
   * own additions as the platform's guess.
   */
  requiredDocuments?: Array<{
    label: string;
    kind: "file" | "text";
    documentType: string | null;
  }>;
}

export function submitProQuote(
  quoteRequestId: string,
  payload: SubmitQuotePayload,
): Promise<{ id: string; priceMinor: number; estimatedDays: number | null; status: MyQuoteStatus }> {
  return apiClient
    .post(`/pro/quote-requests/${quoteRequestId}/quotes`, payload)
    .then((res) => res.data);
}

/**
 * Ask the consumer about an open request.
 *
 * Rejects with 400 `contact_details_blocked` when moderation stops the
 * message — read the kinds off it with `contactDetailKindsFromError`.
 */
export function askProQuoteClarification(
  quoteRequestId: string,
  question: string,
): Promise<QuoteClarification> {
  return apiClient
    .post<QuoteClarification>(`/pro/quote-requests/${quoteRequestId}/clarifications`, { question })
    .then((res) => res.data);
}

export function withdrawProQuote(quoteRequestId: string, quoteId: string): Promise<void> {
  return apiClient
    .post(`/pro/quote-requests/${quoteRequestId}/quotes/${quoteId}/withdraw`)
    .then(() => undefined);
}

export function acceptNotaryRequest(
  id: string,
  body?: { estimatedCompletionDate?: string },
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/accept`, body ?? {})
    .then((res) => res.data);
}

export function setNotaryEstimatedCompletionDate(
  id: string,
  body: { estimatedCompletionDate: string; reason?: string },
): Promise<{ id: string; estimatedCompletionDate: string }> {
  return apiClient
    .post<{ id: string; estimatedCompletionDate: string }>(
      `/notary/requests/${id}/estimated-completion-date`,
      body,
    )
    .then((res) => res.data);
}

export function requestNotaryRequestInfo(
  id: string,
  message: string,
  attachmentIds?: string[],
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/request-info`, {
      message,
      // Omitted rather than sent empty: the field is optional server-side and
      // an empty array would read as "I attached nothing on purpose".
      ...(attachmentIds && attachmentIds.length > 0 ? { attachmentIds } : {}),
    })
    .then((res) => res.data);
}

/**
 * Attach a working document FOR the consumer.
 *
 * Returns the new document's id so the caller can pass it to
 * `requestNotaryRequestInfo` as an attachmentId: the professional uploads
 * first and sends the message second, which keeps request-info JSON and stops
 * a failed upload from discarding typed prose.
 */
export function uploadProfessionalAttachment(
  requestId: string,
  file: File,
  documentType: ProfessionalAttachmentDocumentType,
  note?: string,
  /**
   * A request-for-information message will follow this upload, so the server
   * should not also send its own "you have a new document" push. Only the
   * client knows a second call is coming.
   */
  withMessage = false,
): Promise<{ id: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("documentType", documentType);
  if (note?.trim()) {
    formData.append("note", note.trim());
  }
  if (withMessage) {
    formData.append("withMessage", "true");
  }
  return apiClient
    .post<{ id: string }>(
      `/notary/requests/${encodeURIComponent(requestId)}/attachments`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

export function uploadNotaryCertificate(
  requestId: string,
  file: File,
  certificateValidityDate?: string,
): Promise<{ id: string; status: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (certificateValidityDate) {
    formData.append("certificateValidityDate", certificateValidityDate);
  }
  return apiClient
    .post<{ id: string; status: string }>(
      `/notary/requests/${encodeURIComponent(requestId)}/upload-certificate`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    )
    .then((res) => res.data);
}

/**
 * "La gestión está lista" — the first of the three transitions the
 * professional now owns (spec f462e7d6).
 *
 * NO ADDRESS. The body used to carry a pickup address, a contact phone and a
 * handoff person so Gestionalo could send someone to collect the instrument;
 * the professional delivers it themselves now, and the address that matters
 * is the consumer's, already on the request. The ROUTE NAME is unchanged
 * server-side so the shipped Flutter app survives the cutover.
 */
export function markNotaryGestionReady(
  id: string,
  body: { notes?: string } = {},
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/notify-document-ready`, body)
    .then((res) => res.data);
}

/** "En camino" — the professional has set out to deliver it. */
export function markNotaryShipmentInTransit(
  id: string,
  body: { consumerMessage?: string; internalNotes?: string } = {},
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/mark-in-transit`, body)
    .then((res) => res.data);
}

/** "Entregada" — completes the request. No proof of delivery; the
 *  professional's assertion is the record (spec f462e7d6, RES-3). */
export function markNotaryDelivered(
  id: string,
  body: { trackingNotes?: string; internalNotes?: string } = {},
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/mark-delivered`, body)
    .then((res) => res.data);
}

export function markNotaryRequestCannotComplete(
  id: string,
  notes: string,
): Promise<{ id: string; status: string }> {
  return apiClient
    .post<{ id: string; status: string }>(`/notary/requests/${id}/cannot-complete`, { notes })
    .then((res) => res.data);
}

export function fetchNotaryRequestDocumentBlob(
  requestId: string,
  documentId: string,
): Promise<Blob> {
  return apiClient
    .get<Blob>(
      `/notary/requests/${encodeURIComponent(requestId)}/documents/${encodeURIComponent(documentId)}/file`,
      { responseType: "blob" },
    )
    .then((res) => res.data);
}

export async function downloadNotaryRequestDocumentsZip(requestId: string): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/notary/requests/${encodeURIComponent(requestId)}/documents/archive`,
    { responseType: "blob" },
  );
  const cd = res.headers["content-disposition"];
  let filename = "documentos-solicitud.zip";
  if (typeof cd === "string") {
    const m = cd.match(/filename="([^"]+)"/);
    if (m?.[1]) filename = m[1];
  }
  const url = URL.createObjectURL(res.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
